import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { ApiProductStatus, FeatureFlagUseCase, ProductAlias } from '@ibk/shared-types';
import { CONTA_PJ_CONVITE_BANNER_CONFIG, CONTABILIDADE_CONVITE_BANNER_CONFIG, PRODUCT_BANNER_CONFIGS, SYNERGY_BANNER_CONFIG, } from '../config/bannerAssets';
import { useHappyHolidaysHomeBanner } from './useHappyHolidaysHomeBanner';
const SYNERGY_BANNER_URL = 'https://www.dominiosistemas.com.br/synergy-2026/?utm_source=app_escritorio%20&utm_medium=banner%20&utm_campaign=synergy_2026';
export const useHomeBanners = ({ domain, homeProducts, hasBillingPermission, isBillingProductAvailable, hasFidelityProgramEnablePermission, includeHappyHolidaysBanner = false, }) => {
    const navigate = useNavigate();
    const { isFeatureFlagWhiteListed } = useFeatureFlag();
    const happyHolidaysBanner = useHappyHolidaysHomeBanner(homeProducts?.products?.[ProductAlias.BENEFICIOS], includeHappyHolidaysBanner);
    return useMemo(() => {
        const isProductContracted = (productAlias) => {
            return homeProducts?.products?.[productAlias]?.data?.status === ApiProductStatus.ACTIVE;
        };
        let baseBanners;
        if (domain === 'escritorio' && homeProducts?.products) {
            const baseBannersList = [];
            const isContaPjContracted = isProductContracted(ProductAlias.CONTA_DIGITAL);
            const contaPjBanner = isContaPjContracted
                ? CONTA_PJ_CONVITE_BANNER_CONFIG
                : PRODUCT_BANNER_CONFIGS[ProductAlias.CONTA_DIGITAL];
            baseBannersList.push(contaPjBanner);
            const isCobrancasContracted = isProductContracted(ProductAlias.COBRANCAS);
            const isBillingActive = isBillingProductAvailable && isCobrancasContracted;
            if (hasBillingPermission && !isBillingActive && isContaPjContracted) {
                baseBannersList.push(PRODUCT_BANNER_CONFIGS[ProductAlias.COBRANCAS]);
            }
            const isContabilidadeContracted = isProductContracted(ProductAlias.CONTABILIDADE_DIGITAL);
            const contabilidadeBanner = isContabilidadeContracted
                ? CONTABILIDADE_CONVITE_BANNER_CONFIG
                : PRODUCT_BANNER_CONFIGS[ProductAlias.CONTABILIDADE_DIGITAL];
            baseBannersList.push(contabilidadeBanner);
            const isBeneficiosContracted = isProductContracted(ProductAlias.BENEFICIOS);
            if (!isBeneficiosContracted) {
                baseBannersList.push(PRODUCT_BANNER_CONFIGS[ProductAlias.BENEFICIOS]);
            }
            const isProgramaFidelidadeFeatureEnabled = !!isFeatureFlagWhiteListed(FeatureFlagUseCase.POINTS_PROGRAM_ENABLE_PROGRAM);
            const isProgramaFidelidadeContracted = isProductContracted(ProductAlias.DOMINIO_RECOMPENSAS);
            if (isProgramaFidelidadeFeatureEnabled &&
                hasFidelityProgramEnablePermission === true &&
                !isProgramaFidelidadeContracted) {
                const programaFidelidadeBanner = PRODUCT_BANNER_CONFIGS[ProductAlias.DOMINIO_RECOMPENSAS];
                baseBannersList.push(programaFidelidadeBanner);
            }
            const isSynergyBannerEnabled = !!isFeatureFlagWhiteListed(FeatureFlagUseCase.PLATFORM_SINERGY_BANNER);
            if (isSynergyBannerEnabled) {
                baseBannersList.push(SYNERGY_BANNER_CONFIG);
            }
            baseBanners = baseBannersList;
        }
        else {
            const baseBannersList = [];
            baseBannersList.push(PRODUCT_BANNER_CONFIGS[ProductAlias.CONTA_DIGITAL]);
            if (hasBillingPermission) {
                baseBannersList.push(PRODUCT_BANNER_CONFIGS[ProductAlias.COBRANCAS]);
            }
            const isContabilidadeContracted = isProductContracted(ProductAlias.CONTABILIDADE_DIGITAL);
            if (isContabilidadeContracted) {
                baseBannersList.push(PRODUCT_BANNER_CONFIGS[ProductAlias.CONTABILIDADE_DIGITAL]);
            }
            if (!isProductContracted(ProductAlias.BENEFICIOS)) {
                baseBannersList.push(PRODUCT_BANNER_CONFIGS[ProductAlias.BENEFICIOS]);
            }
            const isProgramaFidelidadeFeatureEnabled = !!isFeatureFlagWhiteListed(FeatureFlagUseCase.POINTS_PROGRAM_ENABLE_PROGRAM);
            const isProgramaFidelidadeContracted = isProductContracted(ProductAlias.DOMINIO_RECOMPENSAS);
            if (isProgramaFidelidadeFeatureEnabled &&
                hasFidelityProgramEnablePermission === true &&
                !isProgramaFidelidadeContracted) {
                baseBannersList.push(PRODUCT_BANNER_CONFIGS[ProductAlias.DOMINIO_RECOMPENSAS]);
            }
            baseBanners = baseBannersList;
        }
        const result = baseBanners.map(banner => {
            if (banner.id === SYNERGY_BANNER_CONFIG.id) {
                return {
                    ...banner,
                    onClick: () => {
                        window.open(SYNERGY_BANNER_URL, '_blank', 'noopener,noreferrer');
                    },
                };
            }
            let onClick;
            switch (banner.productAlias) {
                case ProductAlias.CONTA_DIGITAL:
                    onClick =
                        domain === 'escritorio'
                            ? () => {
                                window.open('https://suporte.dominioatendimento.com/servicos-digitais/faces/servicos-digitais-escritorio.html', '_blank', 'noopener,noreferrer');
                            }
                            : () => navigate('/conta-pj');
                    break;
                case ProductAlias.COBRANCAS:
                    onClick = () => {
                        navigate('/cobrancas');
                    };
                    break;
                case ProductAlias.CONTABILIDADE_DIGITAL:
                    if (domain === 'escritorio') {
                        const isContabilidadeContracted = isProductContracted(ProductAlias.CONTABILIDADE_DIGITAL);
                        onClick = () => {
                            const url = isContabilidadeContracted
                                ? 'https://onvio.com.br/staff/#/loopback?path=%2fservicos-digitais%2ffaces%2fconvite-dominio-contabilidade-digital.html%3FidContratacao%3D4'
                                : 'https://onvio.com.br/staff/#/loopback?path=%2fcontratacao%2ffaces%2fdominio-contabilidade-digital.html%3FidContratacao%3D2';
                            window.open(url, '_blank', 'noopener,noreferrer');
                        };
                    }
                    else {
                        onClick = () => {
                            navigate('/contabilidade-digital/contas-conectadas');
                        };
                    }
                    break;
                case ProductAlias.BENEFICIOS:
                    onClick = () => {
                        const isBeneficiosContracted = isProductContracted(ProductAlias.BENEFICIOS);
                        if (domain === 'escritorio' && isBeneficiosContracted) {
                            window.open('https://suporte.dominioatendimento.com/servicos-digitais/faces/servicos-digitais-cliente.html', '_blank', 'noopener,noreferrer');
                        }
                        else {
                            navigate('/beneficios');
                        }
                    };
                    break;
                case ProductAlias.DOMINIO_RECOMPENSAS:
                    onClick = domain === 'escritorio' ? () => navigate('/dominio-recompensas') : undefined;
                    break;
                default:
                    onClick = undefined;
                    break;
            }
            return {
                ...banner,
                onClick,
            };
        });
        if (happyHolidaysBanner) {
            result.unshift({
                id: happyHolidaysBanner.id,
                productAlias: ProductAlias.BENEFICIOS,
                imageFit: happyHolidaysBanner.imageFit,
                images: {
                    empresa: { png: happyHolidaysBanner.image, webp: happyHolidaysBanner.image },
                    escritorio: { png: happyHolidaysBanner.image, webp: happyHolidaysBanner.image },
                },
                onClick: happyHolidaysBanner.onClick,
            });
        }
        return result;
    }, [
        domain,
        homeProducts,
        navigate,
        hasBillingPermission,
        isBillingProductAvailable,
        isFeatureFlagWhiteListed,
        hasFidelityProgramEnablePermission,
        happyHolidaysBanner,
    ]);
};
